/**
 * The Article Generator: a URL, a search query or pasted text in; a German
 * HKCM article out, with its sources, formalia report and fact-check.
 *
 * The reference workspace page (`.cursor/rules/news-generator-frontend-ui.mdc`):
 *
 * - left, the settings — output, input mode and its fields, tone, graphics —
 *   with the primary action at the foot of the panel;
 * - centre, the run (progress, "Abbrechen", the job log), then the result: the
 *   article card with its editing actions, or the Überblick;
 * - right, the sources the article was written from;
 * - the header holds what applies to the finished article: the sentiment
 *   analysis, the cover image and the Word export.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
    ArrowDown,
    ArrowRight,
    ArrowUp,
    BarChart3,
    Check,
    ChevronDown,
    CircleCheck,
    ClipboardPaste,
    Download,
    ExternalLink,
    Image as ImageIcon,
    Link2,
    ListChecks,
    LoaderCircle,
    MessageCircleQuestionMark,
    Newspaper,
    Pencil,
    Plus,
    RefreshCw,
    ShieldCheck,
    Sparkles,
    Square,
    TrendingDown,
    TrendingUp,
    Undo2,
    X,
    type LucideIcon,
} from 'lucide-react';

import { FORMALIA } from '../data/formaliaSpec';
import { useAuth } from '../hooks/useAuth';
import { afterFailure, fetchStatus, MAX_MISSED_POLLS, usePolling, type PollOutcome } from '../hooks/usePolling';
import { errorDetail } from '../utils/download';
import { reportSessionExpired } from '../utils/session';
import { toast } from '../utils/toast';
import AiEditModal from './AiEditModal';
import { rewritePassage } from './editor/api';
import { FormaliaBadge, type ValidationResult } from './FormaliaBadge';
import { type Infographic, useInfographicJob } from '../hooks/useInfographicJob';
import PageLayout from './layout/PageLayout';
import Alert from './ui/Alert';
import Badge, { type BadgeTone } from './ui/Badge';
import Button from './ui/Button';
import EmptyState from './ui/EmptyState';
import Markdown from './ui/Markdown';
import Modal from './ui/Modal';
import ProgressBar from './ui/ProgressBar';
import SegmentedControl, { type SegmentOption } from './ui/SegmentedControl';
import Tooltip from './ui/Tooltip';
import WordExportButton, { type WordExportRequest } from './ui/WordExportButton';
import {
    CARD,
    CARD_HEADER,
    cn,
    FOCUS_RING,
    FOCUS_RING_INSET,
    HINT,
    ICON_BUTTON,
    INPUT,
    LABEL,
    SECTION_TITLE,
} from './ui/styles';

type JobStatus = 'idle' | 'pending' | 'running' | 'done' | 'error' | 'cancelled';
type InputMode = 'url' | 'query' | 'manual';
type OutputMode = 'full_draft' | 'overview';
type InfographicCount = 'auto' | 1 | 2 | 3 | 4;

interface FaqItem {
    question: string;
    answer: string;
}

/** One source of the job, as the right panel lists it. */
interface Source {
    role?: string;
    url?: string;
    source?: string;
    title?: string;
    published_at?: string;
    snippet?: string;
}

/** One finding of the fact-check; `problem` and `severity` are the model's English enums. */
interface FactCheckIssue {
    claim: string;
    problem: string;
    detail: string;
    severity: string;
}

/** `report_for_output` in src/app/domain/fact_check.py. */
interface FactCheckReport {
    /** False when the check did not run (switched off, or it failed). */
    checked: boolean;
    coherent: boolean;
    issues: FactCheckIssue[];
    summary: string;
    /** Clear errors were corrected in the text before it was shown. */
    correction_applied?: boolean;
}

/** The article as the status route returns it, the V2 fields filled in. */
interface HkcmArticle {
    title: string;
    body: string;
    seo_title: string;
    seo_description: string;
    tags: string[];
    h1_title?: string;
    teaser?: string;
    key_points?: string[];
    body_markdown?: string;
    faq?: FaqItem[];
    category?: string;
    meta_title?: string;
    meta_description?: string;
    validation_report?: ValidationResult[];
    /** Set once the fact-check has run, on the finished job. */
    fact_check?: FactCheckReport | null;
}

/** GET /api/status/{job_id}. */
interface StatusResponse {
    job_id: string;
    status: string;
    /** `step1_collect`, `step1_collect: <detail>`, `step4_hkcm_style`, `fact_check`, `done`, `error`. */
    step: string | null;
    progress: number;
    logs: string[];
    error?: string | null;
    sources?: Source[] | null;
    /** Only jobs from before the one-step pipeline carry these. */
    english_article?: string | null;
    german_article?: string | null;
    hkcm_article?: HkcmArticle | null;
}

/** POST /api/articles/overview: the key facts of one source. */
interface ArticleOverview {
    main_topic?: string;
    tone?: string;
    key_facts?: string[];
    entity_mentions?: string[];
}

/** Body for POST /api/articles/overview: a source URL or the article text. */
interface OverviewRequest {
    text: string;
    title: string;
}

/**
 * Body for POST /api/start. The shape varies by input mode — a pasted article,
 * a search query, or one or more URLs — so the optional fields are the union of
 * what each mode sends.
 */
interface StartArticleRequest {
    max_related: number;
    url?: string;
    urls?: string[];
    manual_title?: string;
    manual_text?: string;
    search_query?: string;
    tone?: string;
    /** The optional FAQ; the backend writes none unless this is true. */
    generate_faq?: boolean;
}

/** POST /api/sentiment-analysis. `overall_sentiment` and every `impact` are English enums. */
interface SentimentResult {
    overall_sentiment: string;
    impacts: { category: string; impact: string; key_points: string[] }[];
    stocks: { name: string; impact: string; reason: string }[];
    summary: string;
}

const OUTPUT_HINTS: Record<OutputMode, string> = {
    full_draft: 'Sammelt die Quellen, schreibt daraus den HKCM-Artikel und prüft die Fakten.',
    overview: 'Hauptthema, Tonlage und Kernfakten einer Quelle – ohne Artikel.',
};

const INPUT_MODES: ReadonlyArray<SegmentOption<InputMode>> = [
    { value: 'url', label: 'URL' },
    { value: 'query', label: 'Suchbegriff' },
    { value: 'manual', label: 'Manuell' },
];

/** The first URL is the main source; up to four more may join it. */
const MAX_URLS = 5;

/**
 * The pipeline's steps as the progress bar names them. `step` is "id" or
 * "id: detail"; a detail follows the step's name ("Quellensuche: …").
 */
const STEP_LABELS: Record<string, { name: string; running: string }> = {
    step1_collect: { name: 'Quellensuche', running: 'Quellensuche läuft …' },
    step4_hkcm_style: { name: 'Artikel', running: 'Artikel wird geschrieben …' },
    fact_check: { name: 'Faktencheck', running: 'Faktencheck läuft …' },
};

/**
 * A poll may be answered from an older snapshot on another instance: a status
 * never gives way to an earlier one (a "running" job does not fall back to "pending").
 */
const STATUS_RANK: Record<string, number> = { idle: 0, pending: 1, running: 2, done: 3, error: 3, cancelled: 3 };

const STOP_FAILED = 'Der Artikel konnte nicht abgebrochen werden. Bitte noch einmal versuchen.';

/** The backend starts a problem line of the job log with one of these, after the time. */
const ERROR_LINE = /\bFehler:/;
const WARNING_LINE = /\bWarnung:/;

/** An `impact` of the sentiment analysis: tinted tile, status-coloured label. */
const IMPACT: Record<string, { label: string; icon: LucideIcon; tile: string; text: string }> = {
    positive: { label: 'Positiv', icon: ArrowUp, tile: 'border-success/30 bg-success-soft', text: 'text-success' },
    negative: { label: 'Negativ', icon: ArrowDown, tile: 'border-danger/30 bg-danger-soft', text: 'text-danger' },
    neutral: { label: 'Neutral', icon: ArrowRight, tile: 'border-line bg-surface-subtle', text: 'text-fg-secondary' },
};

const OVERALL: Record<string, { label: string; tone: BadgeTone; icon: LucideIcon }> = {
    bullish: { label: 'Bullish', tone: 'success', icon: TrendingUp },
    bearish: { label: 'Bearish', tone: 'danger', icon: TrendingDown },
    neutral: { label: 'Neutral', tone: 'neutral', icon: ArrowRight },
};

/** A fact-check finding's `problem`, in the editor's words. */
const FACT_PROBLEM: Record<string, string> = {
    hallucination: 'Nicht in den Quellen',
    unsupported: 'Nicht belegt',
    contradiction: 'Widerspricht den Quellen',
    incoherent: 'Unstimmig',
};

/** A fact-check finding's `severity` (glossary: high/medium/low), worded on its badge. */
const FACT_SEVERITY: Record<string, { label: string; tone: BadgeTone }> = {
    high: { label: 'Hoch', tone: 'danger' },
    medium: { label: 'Mittel', tone: 'warning' },
    low: { label: 'Gering', tone: 'neutral' },
};

/** The Überblick's `tone`, an English enum from the prompt. */
const OVERVIEW_TONES: Record<string, string> = {
    bullish: 'Bullish',
    bearish: 'Bearish',
    neutral: 'Neutral',
    mixed: 'Gemischt',
};

function stepLabel(step: string | null): string {
    if (!step) return 'Läuft …';
    const [id, ...detail] = step.split(': ');
    const known = STEP_LABELS[id];
    if (!known) return step;
    return detail.length > 0 ? `${known.name}: ${detail.join(': ')}` : known.running;
}

function statusLabel(status: JobStatus, step: string | null): string {
    switch (status) {
        case 'idle':
            return 'Bereit';
        case 'pending':
            return 'Wird gestartet …';
        case 'running':
            return stepLabel(step);
        case 'done':
            return 'Fertig';
        case 'error':
            return 'Fehlgeschlagen';
        case 'cancelled':
            return 'Abgebrochen';
        default:
            return status;
    }
}

/** The host of an http(s) URL; null for the pipeline's `user-query://` and `manual://` markers. */
function webHost(url: string | undefined): string | null {
    if (!url) return null;
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.hostname.replace(/^www\./, '') : null;
    } catch {
        return null;
    }
}

/** What a source is: the pipeline marks a search query in `url` and pasted text in `source`. */
function sourceKind(source: Source): { label: string; tone: BadgeTone } {
    if (source.role !== 'primary') return { label: 'Weitere Quelle', tone: 'neutral' };
    if (source.url?.startsWith('user-query://')) return { label: 'Suchbegriff', tone: 'primary' };
    if (source.source === 'Manual Input') return { label: 'Eingefügter Text', tone: 'primary' };
    return { label: 'Hauptquelle', tone: 'primary' };
}

export default function ArticleGenerator() {
    const { isAuthenticated, getToken } = useAuth();

    // Input
    const [outputMode, setOutputMode] = useState<OutputMode>('full_draft');
    const [inputMode, setInputMode] = useState<InputMode>('url');
    const [urls, setUrls] = useState<string[]>(['']);
    const [searchQuery, setSearchQuery] = useState('');
    const [manualTitle, setManualTitle] = useState('');
    const [manualLink, setManualLink] = useState('');
    const [manualText, setManualText] = useState('');
    const [tone, setTone] = useState(''); // blank: the neutral HKCM tone
    // Off by default: hkcm.com runs an FAQ on about one article in forty.
    const [wantFaq, setWantFaq] = useState(false);
    // Ticked before the run, the graphics are built as soon as the article is
    // finished — the standard two to three per article.
    const [wantInfographics, setWantInfographics] = useState(false);
    const [infographicCount, setInfographicCount] = useState<InfographicCount>('auto');
    const [infographicSource, setInfographicSource] = useState('');

    // The job
    const [jobId, setJobId] = useState<string | null>(null);
    const missedPolls = useRef(0);
    const [status, setStatus] = useState<JobStatus>('idle');
    const [step, setStep] = useState<string | null>(null);
    const [progress, setProgress] = useState(0);
    const [logs, setLogs] = useState<string[]>([]);
    const [sources, setSources] = useState<Source[]>([]);
    const [englishArticle, setEnglishArticle] = useState<string | null>(null);
    const [germanArticle, setGermanArticle] = useState<string | null>(null);
    const [article, setArticle] = useState<HkcmArticle | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Editing
    const [isEditing, setIsEditing] = useState(false);
    const [editedTitle, setEditedTitle] = useState('');
    const [editedBody, setEditedBody] = useState('');
    const [editedTeaser, setEditedTeaser] = useState('');
    const [editedKeyPoints, setEditedKeyPoints] = useState<string[]>([]);
    const [editedFaq, setEditedFaq] = useState<FaqItem[]>([]);
    const [undoStack, setUndoStack] = useState<string[]>([]);
    // The server version the edit fields were last filled from. While they still
    // hold exactly that, a newer version replaces them: the fact-check's
    // correction lands after the first draft is already on screen.
    const filledFromServer = useRef<string | null>(null);
    const editedSnapshot = JSON.stringify([editedTitle, editedBody, editedTeaser, editedKeyPoints, editedFaq]);
    const editedSnapshotRef = useRef(editedSnapshot);
    useEffect(() => {
        editedSnapshotRef.current = editedSnapshot;
    }, [editedSnapshot]);

    // "Mit KI bearbeiten": a selection in the title or the body, rewritten
    const titleInputRef = useRef<HTMLInputElement>(null);
    const bodyTextareaRef = useRef<HTMLTextAreaElement>(null);
    const [gptModalOpen, setGptModalOpen] = useState(false);
    const [selectionRange, setSelectionRange] = useState<{ start: number; end: number } | null>(null);
    const [selectedTextPreview, setSelectedTextPreview] = useState('');
    const [editTarget, setEditTarget] = useState<'title' | 'body'>('body');

    // Titelbild
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [imagePromptTweak, setImagePromptTweak] = useState('');
    const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
    const [acceptedImageUrl, setAcceptedImageUrl] = useState<string | null>(null);
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);

    // Stimmungsanalyse
    const [sentimentModalOpen, setSentimentModalOpen] = useState(false);
    const [sentimentResult, setSentimentResult] = useState<SentimentResult | null>(null);
    const [sentimentError, setSentimentError] = useState<string | null>(null);
    const [sentimentLoading, setSentimentLoading] = useState(false);

    // Überblick: the key facts of one source, no article
    const [overview, setOverview] = useState<ArticleOverview | null>(null);
    const [overviewLoading, setOverviewLoading] = useState(false);

    // News-Grafiken: a job of their own, attached to this article's job
    const infographicJob = useInfographicJob(getToken);
    const {
        results: infographics,
        running: isGeneratingInfographics,
        error: infographicError,
    } = infographicJob;
    const infographicsRequestedFor = useRef<string | null>(null);
    const generateInfographicsRef = useRef<() => Promise<void> | void>(() => {});

    const running = status === 'pending' || status === 'running';
    const url = urls[0] ?? '';
    const shownKeyPoints = editedKeyPoints.length > 0 ? editedKeyPoints : (article?.key_points ?? []);
    const shownFaq = editedFaq.length > 0 ? editedFaq : (article?.faq ?? []);
    const primaryLabel = outputMode === 'overview' ? 'Überblick erstellen' : 'Artikel erstellen';
    const inputMissing =
        inputMode === 'manual' ? !manualTitle || !manualLink || !manualText : inputMode === 'query' ? !searchQuery : !url;

    /**
     * A call to the API with the bearer token. Null when the session has
     * expired (signed in without a token, or a 401): the shell shows the banner.
     */
    const api = async (path: string, { method, body }: { method?: 'GET' | 'POST'; body?: unknown } = {}) => {
        const token = await getToken();
        if (isAuthenticated && !token) {
            reportSessionExpired();
            return null;
        }
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        if (body !== undefined) headers['Content-Type'] = 'application/json';
        const response = await fetch(path, {
            method: method ?? (body === undefined ? 'GET' : 'POST'),
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
        });
        if (response.status === 401) {
            reportSessionExpired();
            return null;
        }
        return response;
    };

    /** Forget the article on screen and everything made from it. */
    const clearResult = () => {
        setStep(null);
        setProgress(0);
        setLogs([]);
        setSources([]);
        setEnglishArticle(null);
        setGermanArticle(null);
        setArticle(null);
        filledFromServer.current = null;
        setEditedTitle('');
        setEditedBody('');
        setEditedTeaser('');
        setEditedKeyPoints([]);
        setEditedFaq([]);
        setUndoStack([]);
        setIsEditing(false);
        setGeneratedImageUrl(null);
        setAcceptedImageUrl(null);
        infographicJob.reset();
    };

    const pollStatus = async (signal: AbortSignal): Promise<PollOutcome> => {
        if (!jobId) return;
        const token = await getToken();
        if (isAuthenticated && !token) {
            reportSessionExpired();
            return 'retry';
        }
        // The time stamp keeps a cache from answering.
        const answer = await fetchStatus<StatusResponse>(
            `/api/status/${jobId}?t=${Date.now()}`,
            token ? { Authorization: `Bearer ${token}` } : {},
            signal,
        );
        if (signal.aborted) return;
        if (!answer.ok) {
            if (answer.status === 404 && ++missedPolls.current >= MAX_MISSED_POLLS) {
                // The job is gone (a restart with nothing stored): start afresh.
                clearResult();
                setJobId(null);
                setStatus('idle');
                setError('Der Server wurde neu gestartet, der Auftrag ist verloren. Bitte den Artikel neu erstellen.');
            }
            return afterFailure(answer);
        }
        missedPolls.current = 0;
        const data = answer.data;
        if ((STATUS_RANK[data.status] ?? 0) < (STATUS_RANK[status] ?? 0)) return;

        setStatus(data.status as JobStatus);
        setStep(data.step);
        setProgress(data.progress ?? 0);
        setLogs(data.logs ?? []);
        setSources(data.sources ?? []);
        setEnglishArticle(data.english_article || null);
        setGermanArticle(data.german_article || null);
        setError(data.error || null);

        const next = data.hkcm_article;
        if (next) {
            setArticle(next);
            const serverSnapshot = JSON.stringify([
                next.title,
                next.body,
                next.teaser || '',
                next.key_points || [],
                next.faq || [],
            ]);
            const untouched =
                filledFromServer.current === null || editedSnapshotRef.current === filledFromServer.current;
            if (untouched && serverSnapshot !== filledFromServer.current) {
                filledFromServer.current = serverSnapshot;
                setEditedTitle(next.title);
                setEditedBody(next.body);
                setEditedTeaser(next.teaser || '');
                setEditedKeyPoints(next.key_points || []);
                setEditedFaq(next.faq || []);
                setUndoStack([next.body]);
            }
        }
    };
    usePolling(pollStatus, 1000, jobId !== null && running);

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            if (text) setUrls((prev) => [text.trim(), ...prev.slice(1)]);
        } catch (err) {
            console.error('Clipboard read failed', err);
            toast.warning('Die Zwischenablage konnte nicht gelesen werden. Bitte die URL von Hand einfügen.');
        }
    };

    const handleOverviewRun = async () => {
        let text: string;
        let title = '';
        if (inputMode === 'manual') {
            text = manualText;
            title = manualTitle;
        } else if (inputMode === 'query') {
            toast.warning(
                'Der Überblick fasst einen Artikel zusammen – bitte eine URL oder den Artikeltext (Eingabe „Manuell“) angeben.',
            );
            return;
        } else {
            text = url;
        }
        if (!text) {
            toast.warning('Bitte eine URL oder einen Artikeltext angeben.');
            return;
        }

        setOverviewLoading(true);
        setOverview(null);
        setError(null);
        try {
            const body: OverviewRequest = { text, title };
            const res = await api('/api/articles/overview', { body });
            if (!res) return;
            if (!res.ok) {
                setError((await errorDetail(res)) ?? 'Der Überblick ist fehlgeschlagen.');
                return;
            }
            setOverview((await res.json()) as ArticleOverview);
        } catch (err) {
            console.error('Overview request failed', err);
            setError('Der Überblick ist fehlgeschlagen. Bitte die Verbindung prüfen und erneut versuchen.');
        } finally {
            setOverviewLoading(false);
        }
    };

    const handleStart = async () => {
        if (outputMode === 'overview') {
            await handleOverviewRun();
            return;
        }
        if (inputMode === 'manual' && (!manualTitle || !manualLink || !manualText)) {
            toast.warning('Für die manuelle Eingabe bitte Titel, Quellenlink und Artikeltext angeben.');
            return;
        }
        if (inputMode === 'query' && !searchQuery) {
            toast.warning('Bitte einen Suchbegriff eingeben.');
            return;
        }
        const validUrls = urls.map((value) => value.trim()).filter(Boolean);
        if (inputMode === 'url' && validUrls.length === 0) return;

        // Dropped first: while it still names the previous job, the poll would
        // read that job's "done" and never look at the new one.
        setJobId(null);
        clearResult();
        setStatus('pending');
        setError(null);
        setOverview(null);

        // A blank tone: the backend keeps the neutral HKCM tone.
        const base = {
            max_related: 10,
            ...(tone.trim() ? { tone: tone.trim() } : {}),
            ...(wantFaq ? { generate_faq: true } : {}),
        };
        const body: StartArticleRequest =
            inputMode === 'manual'
                ? { ...base, url: manualLink, manual_title: manualTitle, manual_text: manualText }
                : inputMode === 'query'
                  ? { ...base, url: 'user-query://input', search_query: searchQuery }
                  : { ...base, url: validUrls[0], urls: validUrls };

        try {
            const res = await api('/api/start', { body });
            if (!res) {
                setStatus('idle');
                return;
            }
            if (!res.ok) {
                console.error('Starting the article job failed', res.status);
                setError((await errorDetail(res)) ?? 'Der Artikel konnte nicht gestartet werden. Bitte erneut versuchen.');
                setStatus('error');
                return;
            }
            const data = (await res.json()) as { job_id: string };
            missedPolls.current = 0;
            setJobId(data.job_id);
        } catch (err) {
            console.error('Starting the article job failed', err);
            setError('Der Artikel konnte nicht gestartet werden. Bitte die Verbindung prüfen und erneut versuchen.');
            setStatus('error');
        }
    };

    /** „Abgebrochen“ only once the server agreed: a refused stop leaves the job running, and polled. */
    const handleStop = async () => {
        if (!jobId) return;
        try {
            const res = await api(`/api/stop/${jobId}`, { method: 'POST' });
            if (!res) return;
            if (!res.ok) {
                toast.error((await errorDetail(res)) ?? STOP_FAILED);
                return;
            }
            setStatus('cancelled');
            setLogs((prev) => [...prev, 'Vom Nutzer abgebrochen.']);
        } catch (err) {
            console.error('Stopping the job failed', err);
            toast.error(STOP_FAILED);
        }
    };

    const handleToggleEdit = () => {
        if (!article) return;
        // Entering edit mode starts the undo history at the current text.
        if (!isEditing) setUndoStack([editedBody || article.body]);
        setIsEditing((prev) => !prev);
    };

    const handleUndo = () => {
        if (undoStack.length <= 1) return;
        const stack = undoStack.slice(0, -1);
        setEditedBody(stack[stack.length - 1]);
        setUndoStack(stack);
    };

    const handleBodyChange = (value: string) => {
        setUndoStack((prev) => [...prev, value]);
        setEditedBody(value);
    };

    const handleOpenGptModal = () => {
        const titleInput = titleInputRef.current;
        const bodyTextarea = bodyTextareaRef.current;
        let target: 'title' | 'body' | null = null;
        let start = 0;
        let end = 0;
        // A selection in the title wins; usually only one field holds one.
        if (titleInput && titleInput.selectionStart !== titleInput.selectionEnd) {
            target = 'title';
            start = titleInput.selectionStart ?? 0;
            end = titleInput.selectionEnd ?? 0;
        } else if (bodyTextarea && bodyTextarea.selectionStart !== bodyTextarea.selectionEnd) {
            target = 'body';
            start = bodyTextarea.selectionStart ?? 0;
            end = bodyTextarea.selectionEnd ?? 0;
        }
        if (!target || start === end) {
            toast.info('Zuerst den Text markieren, der geändert werden soll – im Titel oder im Text.');
            return;
        }
        setEditTarget(target);
        setSelectionRange({ start, end });
        setSelectedTextPreview((target === 'title' ? editedTitle : editedBody).substring(start, end));
        setGptModalOpen(true);
    };

    const handleAcceptGptEdit = (replacement: string) => {
        if (!selectionRange) return;
        const splice = (text: string) =>
            text.substring(0, selectionRange.start) + replacement + text.substring(selectionRange.end);
        if (editTarget === 'title') {
            // The title has no undo history.
            setEditedTitle(splice(editedTitle));
        } else {
            handleBodyChange(splice(editedBody));
        }
        setGptModalOpen(false);
    };

    const handleGenerateImage = async () => {
        if (!editedTitle || !editedBody) {
            toast.warning('Für ein Titelbild braucht der Artikel einen Titel und einen Text.');
            return;
        }
        setIsGeneratingImage(true);
        setImageModalOpen(true);
        setGeneratedImageUrl(null);
        try {
            const res = await api('/api/generate-image', {
                body: { title: editedTitle, body: editedBody, user_instructions: imagePromptTweak || undefined },
            });
            if (!res) return;
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = (await res.json()) as { image_url: string };
            setGeneratedImageUrl(data.image_url);
        } catch (err) {
            console.error('Cover image failed', err);
            toast.error('Das Titelbild konnte nicht erstellt werden. Bitte erneut versuchen.');
            setImageModalOpen(false);
        } finally {
            setIsGeneratingImage(false);
        }
    };

    const handleAcceptImage = () => {
        if (!generatedImageUrl) return;
        setAcceptedImageUrl(generatedImageUrl);
        setImageModalOpen(false);
    };

    const handleGenerateInfographics = async () => {
        const bodyText = editedBody || article?.body_markdown || article?.body || '';
        if (!bodyText.trim()) {
            toast.warning('Der Artikel hat noch keinen Text.');
            return;
        }
        await infographicJob.start({
            title: editedTitle || article?.title || '',
            body: bodyText,
            source: infographicSource || undefined,
            count: infographicCount === 'auto' ? undefined : infographicCount,
            job_id: jobId,
        });
    };

    // Keep the ref pointing at the current closure.
    useEffect(() => {
        generateInfographicsRef.current = handleGenerateInfographics;
    });

    // Once per job, when the editor asked for graphics before the run.
    useEffect(() => {
        if (!wantInfographics || status !== 'done' || !jobId) return;
        if (infographicsRequestedFor.current === jobId) return;
        if (!(editedBody || article?.body_markdown)) return;
        infographicsRequestedFor.current = jobId;
        void generateInfographicsRef.current();
        // handleGenerateInfographics is redefined every render, so it is read from
        // a ref rather than listed here — listing it would re-run this effect
        // constantly. The infographicsRequestedFor guard above still limits it to
        // once per job.
    }, [wantInfographics, status, jobId, editedBody, article]);

    const downloadInfographic = (item: Infographic) => {
        const link = document.createElement('a');
        link.href = item.image_url;
        link.download = `hkcm-grafik-${item.index}.png`;
        link.click();
    };

    const handleSentimentAnalysis = async () => {
        const text = editedBody || article?.body_markdown || '';
        if (!text) {
            toast.warning('Es gibt noch keinen Artikeltext zum Analysieren.');
            return;
        }
        setSentimentLoading(true);
        setSentimentModalOpen(true);
        setSentimentResult(null);
        setSentimentError(null);
        try {
            const res = await api('/api/sentiment-analysis', {
                body: { article_text: text, title: editedTitle || article?.title || article?.h1_title || '' },
            });
            if (!res) return;
            if (!res.ok) {
                setSentimentError((await errorDetail(res)) ?? 'Die Stimmungsanalyse ist fehlgeschlagen.');
                return;
            }
            setSentimentResult((await res.json()) as SentimentResult);
        } catch (err) {
            console.error('Sentiment analysis failed', err);
            setSentimentError('Die Stimmungsanalyse ist fehlgeschlagen. Bitte die Verbindung prüfen und erneut versuchen.');
        } finally {
            setSentimentLoading(false);
        }
    };

    const sentimentExport = (): WordExportRequest | null =>
        sentimentResult ? { url: '/api/sentiment/export-docx', body: sentimentResult } : null;

    const articleExport = (): WordExportRequest | null =>
        jobId
            ? {
                  url: `/api/export/${jobId}/docx`,
                  body: {
                      custom_title: editedTitle,
                      custom_text: editedBody,
                      custom_teaser: editedTeaser,
                      custom_key_points: editedKeyPoints,
                      custom_faq: editedFaq,
                      custom_image_url: acceptedImageUrl,
                      infographics: infographics.map((g) => ({
                          image_url: g.image_url,
                          caption: g.caption,
                          anchor_heading: g.anchor_heading,
                      })),
                  },
              }
            : null;

    const hasSeo = Boolean(
        article && (article.meta_title || article.meta_description || article.category || article.tags?.length),
    );
    const showInfographics = wantInfographics || infographics.length > 0 || isGeneratingInfographics;

    return (
        <PageLayout
            view="article-generator"
            variant="workspace"
            actions={
                <>
                    <Button
                        variant="secondary"
                        icon={BarChart3}
                        onClick={() => void handleSentimentAnalysis()}
                        loading={sentimentLoading}
                        disabled={!article || running}
                    >
                        Stimmungsanalyse
                    </Button>
                    <Button
                        variant="secondary"
                        icon={ImageIcon}
                        onClick={() => void handleGenerateImage()}
                        disabled={!article || running}
                    >
                        Titelbild erstellen
                    </Button>
                    <WordExportButton
                        request={articleExport}
                        fallbackName="HKCM_Artikel.docx"
                        disabled={!article || running}
                    />
                </>
            }
        >
            {/* LEFT: the settings, the primary action at the foot (stacked on top when narrow) */}
            <aside
                aria-label="Einstellungen"
                className="flex shrink-0 flex-col border-b border-line bg-surface @min-[45rem]:w-80 @min-[45rem]:border-r @min-[45rem]:border-b-0"
            >
                <div className="p-5 @min-[45rem]:min-h-0 @min-[45rem]:flex-1 @min-[45rem]:overflow-y-auto">
                    <fieldset disabled={running} className="min-w-0 space-y-5">
                        <legend className="sr-only">Eingabe und Optionen</legend>

                        <div>
                            <label htmlFor="ag-output-mode" className={LABEL}>
                                Ausgabe
                            </label>
                            <select
                                id="ag-output-mode"
                                value={outputMode}
                                onChange={(e) => setOutputMode(e.target.value as OutputMode)}
                                className={INPUT}
                            >
                                <option value="full_draft">Vollständiger Artikel</option>
                                <option value="overview">Überblick (nur Kernfakten)</option>
                            </select>
                            <p className={HINT}>{OUTPUT_HINTS[outputMode]}</p>
                        </div>

                        <div className="space-y-3">
                            {/* The group below carries the same name for assistive tech. */}
                            <p className={cn(LABEL, 'mb-0')} aria-hidden="true">
                                Eingabe
                            </p>
                            <SegmentedControl
                                label="Eingabe"
                                options={INPUT_MODES}
                                value={inputMode}
                                onChange={setInputMode}
                                full
                                size="sm"
                            />

                            {inputMode === 'url' && (
                                <div className="space-y-2 animate-fadeIn">
                                    {urls.map((value, index) => (
                                        <div key={index} className="relative">
                                            <input
                                                type="url"
                                                aria-label={index === 0 ? 'Artikel-URL' : `Weitere URL ${index + 1}`}
                                                placeholder={index === 0 ? 'Artikel-URL einfügen …' : 'Weitere Artikel-URL …'}
                                                className={cn(INPUT, 'pr-10')}
                                                value={value}
                                                onChange={(e) =>
                                                    setUrls((prev) =>
                                                        prev.map((item, i) => (i === index ? e.target.value : item)),
                                                    )
                                                }
                                            />
                                            {urls.length === 1 ? (
                                                <Tooltip
                                                    label="Aus der Zwischenablage einfügen"
                                                    side="bottom"
                                                    className="absolute right-1 top-1/2 -translate-y-1/2"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => void handlePasteFromClipboard()}
                                                        aria-label="Aus der Zwischenablage einfügen"
                                                        className={cn(ICON_BUTTON, 'h-7 w-7')}
                                                    >
                                                        <ClipboardPaste className="h-4 w-4" aria-hidden="true" />
                                                    </button>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip
                                                    label="URL entfernen"
                                                    side="bottom"
                                                    className="absolute right-1 top-1/2 -translate-y-1/2"
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => setUrls((prev) => prev.filter((_, i) => i !== index))}
                                                        aria-label={`URL ${index + 1} entfernen`}
                                                        className={cn(ICON_BUTTON, 'h-7 w-7 hover:text-danger')}
                                                    >
                                                        <X className="h-4 w-4" aria-hidden="true" />
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </div>
                                    ))}
                                    {urls.length < MAX_URLS && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            icon={Plus}
                                            className="w-full text-primary hover:text-primary-hover"
                                            onClick={() => setUrls((prev) => [...prev, ''])}
                                        >
                                            URL hinzufügen
                                        </Button>
                                    )}
                                    <p className={HINT}>
                                        Die erste URL ist die Hauptquelle. Ohne weitere URLs sucht das Tool passende
                                        Berichte selbst.
                                    </p>
                                </div>
                            )}

                            {inputMode === 'query' && (
                                <div className="animate-fadeIn">
                                    <textarea
                                        aria-label="Suchbegriff"
                                        placeholder="Thema oder Frage, z. B. „Warum fällt die Nvidia-Aktie heute?“"
                                        className={cn(INPUT, 'min-h-[100px]')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                    <p className={HINT}>Eine konkrete Frage findet passendere Quellen als ein Stichwort.</p>
                                </div>
                            )}

                            {inputMode === 'manual' && (
                                <div className="space-y-3 animate-fadeIn">
                                    <div>
                                        <label htmlFor="ag-manual-title" className={LABEL}>
                                            Titel
                                        </label>
                                        <input
                                            id="ag-manual-title"
                                            type="text"
                                            className={INPUT}
                                            value={manualTitle}
                                            onChange={(e) => setManualTitle(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="ag-manual-link" className={LABEL}>
                                            Quellenlink
                                        </label>
                                        <input
                                            id="ag-manual-link"
                                            type="url"
                                            placeholder="https://…"
                                            className={INPUT}
                                            value={manualLink}
                                            onChange={(e) => setManualLink(e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="ag-manual-text" className={LABEL}>
                                            Artikeltext
                                        </label>
                                        <textarea
                                            id="ag-manual-text"
                                            placeholder="Den vollständigen Artikeltext einfügen …"
                                            className={cn(INPUT, 'min-h-[150px]')}
                                            value={manualText}
                                            onChange={(e) => setManualText(e.target.value)}
                                        />
                                        <p className={HINT}>Alle drei Felder sind Pflicht.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label htmlFor="ag-tone" className={LABEL}>
                                Tonalität <span className="font-normal text-fg-muted">(optional)</span>
                            </label>
                            <input
                                id="ag-tone"
                                type="text"
                                placeholder="z. B. „sachlich-nüchtern“"
                                className={INPUT}
                                value={tone}
                                onChange={(e) => setTone(e.target.value)}
                            />
                            <p className={HINT}>Leer lassen für den neutralen HKCM-Ton.</p>
                        </div>

                        {outputMode === 'full_draft' && (
                            <div className="rounded-xl border border-line bg-surface-subtle p-3">
                                <label className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-fg">
                                    <input
                                        type="checkbox"
                                        checked={wantFaq}
                                        onChange={(e) => setWantFaq(e.target.checked)}
                                        aria-describedby="ag-faq-hint"
                                        className="h-4 w-4 shrink-0 rounded-sm accent-primary"
                                    />
                                    <MessageCircleQuestionMark className="h-4 w-4 text-primary" aria-hidden="true" />
                                    FAQ erzeugen
                                </label>
                                <p id="ag-faq-hint" className={cn(HINT, 'pl-6')}>
                                    Bis zu {FORMALIA.faq.max} Fragen mit Antworten für das FAQ-Feld im CMS.
                                </p>
                            </div>
                        )}

                        <div className="rounded-xl border border-line bg-surface-subtle p-3">
                            <label className="flex cursor-pointer items-center gap-2.5 text-sm font-semibold text-fg">
                                <input
                                    type="checkbox"
                                    checked={wantInfographics}
                                    onChange={(e) => setWantInfographics(e.target.checked)}
                                    className="h-4 w-4 shrink-0 rounded-sm accent-primary"
                                />
                                <ImageIcon className="h-4 w-4 text-primary" aria-hidden="true" />
                                News-Grafiken erstellen
                            </label>
                            {wantInfographics && (
                                <div className="mt-3 space-y-2 pl-6">
                                    <select
                                        aria-label="Anzahl der Grafiken"
                                        value={String(infographicCount)}
                                        onChange={(e) =>
                                            setInfographicCount(
                                                e.target.value === 'auto'
                                                    ? 'auto'
                                                    : (Number(e.target.value) as InfographicCount),
                                            )
                                        }
                                        className={INPUT}
                                    >
                                        <option value="auto">Automatisch (2 bis 3)</option>
                                        <option value="1">1 Grafik</option>
                                        <option value="2">2 Grafiken</option>
                                        <option value="3">3 Grafiken</option>
                                        <option value="4">4 Grafiken</option>
                                    </select>
                                    <input
                                        type="text"
                                        aria-label="Quelle für die Grafiken"
                                        placeholder="Quelle für die Grafiken (optional)"
                                        value={infographicSource}
                                        onChange={(e) => setInfographicSource(e.target.value)}
                                        className={INPUT}
                                    />
                                </div>
                            )}
                        </div>
                    </fieldset>
                </div>

                <div className="border-t border-line p-5">
                    <Button
                        size="lg"
                        className="w-full"
                        onClick={() => void handleStart()}
                        loading={running || overviewLoading}
                        disabled={inputMissing}
                    >
                        {primaryLabel}
                    </Button>
                </div>
            </aside>

            {/* CENTRE and RIGHT: the result, the sources beside it from 68rem, below it until then */}
            <div className="min-w-0 flex-1 @min-[45rem]:overflow-y-auto @min-[68rem]:flex @min-[68rem]:overflow-hidden">
                <section aria-label="Ergebnis" className="min-w-0 space-y-4 p-6 @min-[68rem]:flex-1 @min-[68rem]:overflow-y-auto">
                    {status !== 'idle' && (
                        <div className={cn(CARD, 'space-y-4 p-4')}>
                            <div className="flex items-center gap-4">
                                <ProgressBar
                                    className="min-w-0 flex-1"
                                    label={statusLabel(status, step)}
                                    value={progress * 100}
                                    busy={running}
                                />
                                {running && (
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={Square}
                                        onClick={() => void handleStop()}
                                        disabled={!jobId}
                                    >
                                        Abbrechen
                                    </Button>
                                )}
                            </div>
                            <JobLog lines={logs} />
                        </div>
                    )}

                    {error && (
                        <Alert tone="error" title="Fehler" onDismiss={() => setError(null)}>
                            <Markdown size="sm">{error}</Markdown>
                        </Alert>
                    )}

                    {englishArticle && <Disclosure title="Konsolidierter Artikel (Englisch)">{englishArticle}</Disclosure>}
                    {germanArticle && <Disclosure title="Deutsche Übersetzung">{germanArticle}</Disclosure>}

                    {article && (
                        <article aria-labelledby="ag-article-heading" className={CARD}>
                            {/* Sticky, so the editing actions stay at hand in a long body; -top-6
                                cancels the section's padding, which a sticky element keeps clear. */}
                            <div className={cn(CARD_HEADER, 'sticky -top-6 z-10 rounded-t-2xl bg-surface')}>
                                <h2
                                    id="ag-article-heading"
                                    className="flex items-center gap-2 text-base font-semibold text-fg-strong"
                                >
                                    <Newspaper className="h-5 w-5 text-primary" aria-hidden="true" />
                                    HKCM-Artikel
                                    {running && <Badge tone="info">Wird geprüft …</Badge>}
                                </h2>
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                    {isEditing && (
                                        <>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                icon={Undo2}
                                                onClick={handleUndo}
                                                disabled={undoStack.length <= 1}
                                            >
                                                Rückgängig
                                            </Button>
                                            <Button variant="secondary" size="sm" icon={Sparkles} onClick={handleOpenGptModal}>
                                                Mit KI bearbeiten
                                            </Button>
                                        </>
                                    )}
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        icon={isEditing ? Check : Pencil}
                                        onClick={handleToggleEdit}
                                    >
                                        {isEditing ? 'Fertig' : 'Bearbeiten'}
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-6 p-6">
                                <FormaliaBadge report={article.validation_report} article={article} />

                                {article.fact_check && <FactCheckCard report={article.fact_check} />}

                                {isEditing ? (
                                    <div>
                                        <label htmlFor="ag-edit-title" className={LABEL}>
                                            Titel (H1)
                                        </label>
                                        <input
                                            id="ag-edit-title"
                                            ref={titleInputRef}
                                            className={cn(INPUT, 'text-2xl font-bold')}
                                            value={editedTitle}
                                            onChange={(e) => setEditedTitle(e.target.value)}
                                        />
                                    </div>
                                ) : (
                                    <h3 className="text-2xl font-bold leading-tight text-fg-strong">
                                        {editedTitle || article.title}
                                    </h3>
                                )}

                                {acceptedImageUrl && (
                                    <div className="group relative">
                                        <img
                                            src={acceptedImageUrl}
                                            alt="Titelbild"
                                            className="h-auto w-full rounded-xl shadow-card"
                                        />
                                        <Tooltip label="Titelbild entfernen" side="bottom" className="absolute right-2 top-2">
                                            <button
                                                type="button"
                                                onClick={() => setAcceptedImageUrl(null)}
                                                aria-label="Titelbild entfernen"
                                                className={cn(
                                                    ICON_BUTTON,
                                                    'bg-surface text-fg opacity-0 shadow-xs transition-opacity hover:text-danger focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100',
                                                )}
                                            >
                                                <X className="h-4 w-4" aria-hidden="true" />
                                            </button>
                                        </Tooltip>
                                    </div>
                                )}

                                {isEditing ? (
                                    <div>
                                        <label htmlFor="ag-edit-teaser" className={LABEL}>
                                            Teaser
                                        </label>
                                        <textarea
                                            id="ag-edit-teaser"
                                            className={cn(INPUT, 'min-h-20')}
                                            value={editedTeaser}
                                            onChange={(e) => setEditedTeaser(e.target.value)}
                                        />
                                    </div>
                                ) : (
                                    (editedTeaser || article.teaser) && (
                                        <p className="border-l-4 border-primary pl-4 text-base font-medium italic leading-relaxed text-fg-secondary">
                                            {editedTeaser || article.teaser}
                                        </p>
                                    )
                                )}

                                {(isEditing ? editedKeyPoints.length > 0 : shownKeyPoints.length > 0) && (
                                    <section
                                        aria-labelledby="ag-key-points"
                                        className="rounded-xl border border-line bg-surface-subtle p-5"
                                    >
                                        <h4 id="ag-key-points" className={cn(SECTION_TITLE, 'mb-3')}>
                                            Kernpunkte
                                        </h4>
                                        <ul className="space-y-2">
                                            {isEditing
                                                ? editedKeyPoints.map((point, i) => (
                                                      <li key={i}>
                                                          <input
                                                              aria-label={`Kernpunkt ${i + 1}`}
                                                              className={INPUT}
                                                              value={point}
                                                              onChange={(e) =>
                                                                  setEditedKeyPoints((prev) =>
                                                                      prev.map((item, j) => (j === i ? e.target.value : item)),
                                                                  )
                                                              }
                                                          />
                                                      </li>
                                                  ))
                                                : shownKeyPoints.map((point, i) => (
                                                      <li key={i} className="flex gap-2 text-sm leading-snug text-fg">
                                                          <span className="font-bold text-primary" aria-hidden="true">
                                                              •
                                                          </span>
                                                          <span>{point}</span>
                                                      </li>
                                                  ))}
                                        </ul>
                                    </section>
                                )}

                                {showInfographics && (
                                    <InfographicsPanel
                                        items={infographics}
                                        busy={isGeneratingInfographics}
                                        step={infographicJob.step}
                                        progress={infographicJob.progress}
                                        error={infographicError}
                                        onGenerate={() => void handleGenerateInfographics()}
                                        onStop={() => void infographicJob.stop()}
                                        onDownload={downloadInfographic}
                                    />
                                )}

                                <hr className="border-line" />

                                {isEditing ? (
                                    <div>
                                        <label htmlFor="ag-edit-body" className={LABEL}>
                                            Text
                                        </label>
                                        <textarea
                                            id="ag-edit-body"
                                            ref={bodyTextareaRef}
                                            rows={18}
                                            className={cn(INPUT, 'min-h-[500px] text-base leading-relaxed')}
                                            value={editedBody}
                                            onChange={(e) => handleBodyChange(e.target.value)}
                                        />
                                    </div>
                                ) : (
                                    <Markdown className="prose-h2:mb-3 prose-h2:mt-8 prose-h2:text-lg">
                                        {editedBody || article.body || ''}
                                    </Markdown>
                                )}

                                {shownFaq.length > 0 && (
                                    <section aria-labelledby="ag-faq" className="space-y-4 border-t border-line pt-6">
                                        <h4 id="ag-faq" className="text-base font-semibold text-fg-strong">
                                            FAQ
                                        </h4>
                                        <div className="space-y-3">
                                            {shownFaq.map((item, i) => (
                                                <div key={i} className="rounded-xl bg-surface-subtle p-4">
                                                    {isEditing ? (
                                                        <div className="space-y-2">
                                                            <input
                                                                aria-label={`Frage ${i + 1}`}
                                                                placeholder="Frage"
                                                                className={cn(INPUT, 'font-semibold')}
                                                                value={item.question}
                                                                onChange={(e) =>
                                                                    setEditedFaq((prev) =>
                                                                        prev.map((faq, j) =>
                                                                            j === i ? { ...faq, question: e.target.value } : faq,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                            <textarea
                                                                aria-label={`Antwort ${i + 1}`}
                                                                placeholder="Antwort"
                                                                rows={3}
                                                                className={INPUT}
                                                                value={item.answer}
                                                                onChange={(e) =>
                                                                    setEditedFaq((prev) =>
                                                                        prev.map((faq, j) =>
                                                                            j === i ? { ...faq, answer: e.target.value } : faq,
                                                                        ),
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <p className="mb-1 font-semibold text-fg-strong">{item.question}</p>
                                                            <p className="text-sm text-fg-secondary">{item.answer}</p>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </section>
                                )}

                                {hasSeo && (
                                    <section aria-labelledby="ag-seo" className="space-y-4 border-t border-line pt-6">
                                        <h4 id="ag-seo" className="text-base font-semibold text-fg-strong">
                                            SEO und Metadaten
                                        </h4>
                                        <dl className="grid grid-cols-[9rem_minmax(0,1fr)] gap-x-4 gap-y-3 text-sm">
                                            {article.meta_title && (
                                                <>
                                                    <dt className="font-semibold text-fg-muted">Meta-Titel</dt>
                                                    <dd className="text-fg">{article.meta_title}</dd>
                                                </>
                                            )}
                                            {article.meta_description && (
                                                <>
                                                    <dt className="font-semibold text-fg-muted">Meta-Beschreibung</dt>
                                                    <dd className="text-fg">{article.meta_description}</dd>
                                                </>
                                            )}
                                            {article.category && (
                                                <>
                                                    <dt className="font-semibold text-fg-muted">Kategorie</dt>
                                                    <dd>
                                                        <Badge tone="primary">{article.category}</Badge>
                                                    </dd>
                                                </>
                                            )}
                                            {article.tags?.length > 0 && (
                                                <>
                                                    <dt className="font-semibold text-fg-muted">Tags</dt>
                                                    <dd className="flex flex-wrap gap-1.5">
                                                        {article.tags.map((tag, i) => (
                                                            <Badge key={i}>{tag}</Badge>
                                                        ))}
                                                    </dd>
                                                </>
                                            )}
                                        </dl>
                                    </section>
                                )}
                            </div>
                        </article>
                    )}

                    {overviewLoading && (
                        <div className={cn(CARD, 'p-4')}>
                            <ProgressBar label="Überblick wird erstellt …" />
                        </div>
                    )}

                    {overview && <OverviewCard overview={overview} onClose={() => setOverview(null)} />}

                    {status === 'idle' && !article && !overview && !overviewLoading && (
                        <EmptyState
                            icon={outputMode === 'overview' ? ListChecks : Newspaper}
                            title={outputMode === 'overview' ? 'Noch kein Überblick' : 'Noch kein Artikel'}
                            description={`Eine URL, einen Suchbegriff oder den Artikeltext eingeben und „${primaryLabel}“ klicken.`}
                        />
                    )}
                </section>

                <aside
                    aria-label="Quellen"
                    className="flex flex-col border-t border-line bg-surface @min-[68rem]:w-80 @min-[68rem]:shrink-0 @min-[68rem]:border-t-0 @min-[68rem]:border-l"
                >
                    <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
                        <h2 className="text-base font-semibold text-fg-strong">Quellen</h2>
                        {sources.length > 0 && <Badge>{sources.length}</Badge>}
                    </div>
                    <div className="p-5 @min-[68rem]:min-h-0 @min-[68rem]:flex-1 @min-[68rem]:overflow-y-auto">
                        {sources.length > 0 ? (
                            <ul className="space-y-3">
                                {sources.map((source, i) => (
                                    <SourceCard key={i} source={source} />
                                ))}
                            </ul>
                        ) : (
                            <EmptyState
                                icon={Link2}
                                title="Noch keine Quellen"
                                description="Die gesammelten Quellen erscheinen hier."
                                className="px-4 py-10"
                            />
                        )}
                    </div>
                </aside>
            </div>

            {gptModalOpen && (
                <AiEditModal
                    passage={selectedTextPreview}
                    suggest={async (instruction) =>
                        rewritePassage(
                            editTarget === 'title' ? editedTitle : editedBody,
                            selectedTextPreview,
                            instruction,
                            await getToken(),
                        )
                    }
                    onClose={() => setGptModalOpen(false)}
                    onAccept={handleAcceptGptEdit}
                />
            )}

            <Modal
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                title="Titelbild erstellen"
                icon={ImageIcon}
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setImageModalOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleAcceptImage} disabled={!generatedImageUrl || isGeneratingImage}>
                            Übernehmen
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            void handleGenerateImage();
                        }}
                    >
                        <label htmlFor="ag-cover-tweak" className={LABEL}>
                            Eigene Vorgaben <span className="font-normal text-fg-muted">(optional)</span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                id="ag-cover-tweak"
                                type="text"
                                className={INPUT}
                                placeholder="z. B. „in Blau und Gold“ oder „Ölfass im Mittelpunkt“"
                                value={imagePromptTweak}
                                onChange={(e) => setImagePromptTweak(e.target.value)}
                            />
                            <Button
                                type="submit"
                                variant="secondary"
                                icon={RefreshCw}
                                disabled={isGeneratingImage}
                                className="shrink-0"
                            >
                                Neu erstellen
                            </Button>
                        </div>
                    </form>
                    <div className="flex min-h-[300px] flex-col items-center justify-center rounded-xl bg-surface-sunken p-4">
                        {isGeneratingImage ? (
                            <div role="status" className="flex flex-col items-center gap-3 text-sm text-fg-secondary">
                                <LoaderCircle className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
                                Titelbild wird erstellt …
                            </div>
                        ) : generatedImageUrl ? (
                            <img
                                src={generatedImageUrl}
                                alt="Vorschau des Titelbilds"
                                className="h-auto w-full rounded-lg shadow-card"
                            />
                        ) : (
                            <p className="text-center text-sm text-fg-secondary">
                                Das Titelbild entsteht aus Titel und Text des Artikels.
                            </p>
                        )}
                    </div>
                </div>
            </Modal>

            <Modal
                open={sentimentModalOpen}
                onClose={() => setSentimentModalOpen(false)}
                title="Stimmungsanalyse"
                description="Auswirkungen des Artikels auf Märkte, Anlageklassen und einzelne Aktien"
                icon={BarChart3}
                size="lg"
                footer={
                    <>
                        {sentimentResult && (
                            <WordExportButton
                                className="mr-auto"
                                request={sentimentExport}
                                fallbackName="HKCM_Stimmungsanalyse.docx"
                                errorMessage="Der Word-Export der Stimmungsanalyse ist fehlgeschlagen."
                            />
                        )}
                        <Button variant="secondary" onClick={() => setSentimentModalOpen(false)}>
                            Schließen
                        </Button>
                    </>
                }
            >
                {sentimentLoading ? (
                    <div role="status" className="flex flex-col items-center gap-3 py-16 text-sm text-fg-secondary">
                        <LoaderCircle className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
                        Die Marktauswirkungen werden analysiert …
                    </div>
                ) : sentimentError ? (
                    <Alert tone="error">{sentimentError}</Alert>
                ) : sentimentResult ? (
                    <SentimentView result={sentimentResult} />
                ) : (
                    <p className="py-12 text-center text-sm text-fg-secondary">Keine Analyse vorhanden.</p>
                )}
            </Modal>
        </PageLayout>
    );
}

/** The job log: every step as the server wrote it, problems marked. */
function JobLog({ lines }: { lines: string[] }) {
    return (
        <div
            role="log"
            aria-label="Protokoll"
            // Scrollable, so reachable by keyboard.
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable log must be reachable by keyboard (WCAG 2.1.1)
            tabIndex={0}
            className={cn(
                'max-h-40 overflow-y-auto rounded-lg bg-surface-subtle px-3 py-2 text-xs leading-relaxed tabular-nums text-fg-secondary',
                FOCUS_RING_INSET,
            )}
        >
            {lines.length === 0 ? (
                <p className="py-1 text-fg-muted">Noch keine Meldungen.</p>
            ) : (
                <ol className="divide-y divide-line">
                    {lines.map((line, i) => (
                        <li
                            key={i}
                            className={cn(
                                'py-1',
                                ERROR_LINE.test(line) && 'font-semibold text-danger',
                                !ERROR_LINE.test(line) && WARNING_LINE.test(line) && 'font-semibold text-warning',
                            )}
                        >
                            {line}
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}

/**
 * The fact-check of the article: its verdict, then each finding with its
 * weight, the claim and why. A check that did not run says so in German — its
 * own summary is a technical note in English then.
 */
function FactCheckCard({ report }: { report: FactCheckReport }) {
    const issues = report.checked ? (report.issues ?? []) : [];
    const note = report.checked
        ? report.summary
        : 'Der Faktencheck ist für diesen Artikel nicht gelaufen. Zahlen, Namen und Zitate bitte selbst mit den Quellen abgleichen.';
    const word = (value: unknown) => String(value ?? '').trim().toLowerCase();
    return (
        <section aria-labelledby="ag-fact-check" className="rounded-2xl border border-line bg-surface-subtle p-4 animate-fadeIn">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 id="ag-fact-check" className="flex items-center gap-2 text-sm font-semibold text-fg-strong">
                    <ShieldCheck className="h-4 w-4 text-primary" aria-hidden="true" />
                    Faktencheck
                </h3>
                {!report.checked ? (
                    <Badge>Nicht geprüft</Badge>
                ) : issues.length === 0 ? (
                    <Badge tone="success" icon={CircleCheck}>
                        Geprüft – keine Beanstandungen
                    </Badge>
                ) : (
                    <Badge tone="warning">{issues.length === 1 ? '1 Hinweis' : `${issues.length} Hinweise`}</Badge>
                )}
            </div>
            {note && <p className="mt-2 text-xs leading-relaxed text-fg-secondary">{note}</p>}
            {report.checked && report.correction_applied && (
                <p className="mt-1 text-xs font-semibold text-fg">Eindeutige Fehler sind im Text bereits korrigiert.</p>
            )}
            {issues.length > 0 && (
                <ul className="mt-3 space-y-2 text-xs">
                    {issues.map((issue, i) => {
                        const severity = FACT_SEVERITY[word(issue.severity)] ?? FACT_SEVERITY.low;
                        return (
                            <li key={i} className="rounded-lg border border-line bg-surface p-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge tone={severity.tone}>
                                        <span className="sr-only">Gewicht: </span>
                                        {severity.label}
                                    </Badge>
                                    <span className="font-semibold text-fg-strong">
                                        {FACT_PROBLEM[word(issue.problem)] ?? 'Hinweis'}
                                    </span>
                                </div>
                                {issue.claim && <p className="mt-1.5 text-fg">„{issue.claim}“</p>}
                                {issue.detail && <p className="mt-1 text-fg-secondary">{issue.detail}</p>}
                            </li>
                        );
                    })}
                </ul>
            )}
        </section>
    );
}

/** A folded block of plain text: the English and German drafts older jobs still carry. */
function Disclosure({ title, children }: { title: string; children: ReactNode }) {
    const [open, setOpen] = useState(false);
    const id = useId();
    return (
        <section className={cn(CARD, 'overflow-hidden')}>
            <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                aria-expanded={open}
                aria-controls={id}
                className={cn(
                    'flex w-full items-center justify-between gap-3 px-5 py-3 text-left text-sm font-semibold text-fg transition-colors hover:bg-surface-subtle',
                    FOCUS_RING_INSET,
                )}
            >
                {title}
                <ChevronDown
                    className={cn('h-4 w-4 shrink-0 text-fg-muted transition-transform', open && 'rotate-180')}
                    aria-hidden="true"
                />
            </button>
            <div
                id={id}
                hidden={!open}
                className="whitespace-pre-wrap border-t border-line px-5 py-4 text-sm leading-relaxed text-fg-secondary"
            >
                {children}
            </div>
        </section>
    );
}

function SourceCard({ source }: { source: Source }) {
    const kind = sourceKind(source);
    const host = webHost(source.url);
    // A search query arrives as the "title" of the primary source.
    const title = source.url?.startsWith('user-query://') ? source.title?.replace(/^Search Query:\s*/, '') : source.title;
    return (
        <li
            className={cn(
                'rounded-xl border p-4',
                source.role === 'primary' ? 'border-primary/30 bg-primary-soft' : 'border-line bg-surface',
            )}
        >
            <div className="mb-2 flex items-center justify-between gap-2">
                <Badge tone={kind.tone}>{kind.label}</Badge>
                {source.published_at && <span className="text-xs text-fg-muted">{source.published_at}</span>}
            </div>
            {title && <p className="text-sm font-semibold leading-snug text-fg-strong">{title}</p>}
            {host && (
                <p className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs">
                    <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                            'inline-flex shrink-0 items-center gap-1 rounded-sm font-semibold text-primary hover:text-primary-hover hover:underline',
                            FOCUS_RING,
                        )}
                    >
                        Quelle öffnen
                        <ExternalLink className="h-3 w-3" aria-hidden="true" />
                        <span className="sr-only">
                            {title ? `: ${title}` : ''} (neuer Tab)
                        </span>
                    </a>
                    <span className="truncate text-fg-muted">· {host}</span>
                </p>
            )}
            {source.snippet && (
                <p className="mt-2 line-clamp-3 border-t border-line pt-2 text-xs leading-relaxed text-fg-secondary">
                    {source.snippet}
                </p>
            )}
        </li>
    );
}

function OverviewCard({ overview, onClose }: { overview: ArticleOverview; onClose: () => void }) {
    return (
        <section aria-labelledby="ag-overview-heading" className={CARD}>
            <div className={CARD_HEADER}>
                <h2 id="ag-overview-heading" className="flex items-center gap-2 text-base font-semibold text-fg-strong">
                    <ListChecks className="h-5 w-5 text-primary" aria-hidden="true" />
                    Überblick
                </h2>
                <button type="button" onClick={onClose} aria-label="Überblick schließen" className={ICON_BUTTON}>
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            </div>
            <dl className="space-y-4 p-5 text-sm">
                <div>
                    <dt className={SECTION_TITLE}>Hauptthema</dt>
                    <dd className="mt-1 text-fg">{overview.main_topic}</dd>
                </div>
                <div>
                    <dt className={SECTION_TITLE}>Tonlage</dt>
                    <dd className="mt-1 text-fg">
                        {overview.tone ? (OVERVIEW_TONES[overview.tone] ?? overview.tone) : '–'}
                    </dd>
                </div>
                <div>
                    <dt className={SECTION_TITLE}>Kernfakten</dt>
                    <dd className="mt-1">
                        <ul className="list-disc space-y-1 pl-5 text-fg">
                            {overview.key_facts?.map((fact, i) => (
                                <li key={i}>{fact}</li>
                            ))}
                        </ul>
                    </dd>
                </div>
                {overview.entity_mentions && overview.entity_mentions.length > 0 && (
                    <div>
                        <dt className={SECTION_TITLE}>Genannt</dt>
                        <dd className="mt-1.5 flex flex-wrap gap-1.5">
                            {overview.entity_mentions.map((entity, i) => (
                                <Badge key={i} tone="primary">
                                    {entity}
                                </Badge>
                            ))}
                        </dd>
                    </div>
                )}
            </dl>
        </section>
    );
}

function InfographicsPanel({
    items,
    busy,
    step,
    progress,
    error,
    onGenerate,
    onStop,
    onDownload,
}: {
    items: Infographic[];
    busy: boolean;
    step: string | null;
    progress: number;
    error: string | null;
    onGenerate: () => void;
    onStop: () => void;
    onDownload: (item: Infographic) => void;
}) {
    const hint = busy
        ? 'Das dauert etwa eine bis zwei Minuten.'
        : items.length === 1
          ? '1 Grafik, wird in den Word-Export eingebettet.'
          : items.length > 1
            ? `${items.length} Grafiken, werden in den Word-Export eingebettet.`
            : 'Grafiken im HKCM-Design; sie kommen auch in den Word-Export.';
    return (
        <section aria-labelledby="ag-infographics" className="space-y-4 rounded-xl border border-line bg-surface-subtle p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <h4 id="ag-infographics" className="flex items-center gap-2 text-sm font-semibold text-fg-strong">
                        <ImageIcon className="h-4 w-4 text-primary" aria-hidden="true" />
                        News-Grafiken
                    </h4>
                    <p className={HINT}>{hint}</p>
                </div>
                <Button
                    variant="secondary"
                    size="sm"
                    icon={items.length > 0 ? RefreshCw : undefined}
                    loading={busy}
                    onClick={onGenerate}
                >
                    {items.length > 0 ? 'Neu erstellen' : 'Grafiken erstellen'}
                </Button>
            </div>

            {busy && (
                <div className="flex items-center gap-3">
                    <ProgressBar className="min-w-0 flex-1" label={step ?? 'Grafiken werden erstellt …'} value={progress} />
                    <Button variant="secondary" size="sm" onClick={onStop}>
                        Abbrechen
                    </Button>
                </div>
            )}

            {error && <Alert tone="error">{error}</Alert>}

            {items.map((item) => (
                <div key={item.index} className="overflow-hidden rounded-lg border border-line bg-surface">
                    <div className="flex items-start justify-between gap-3 border-b border-line px-3 py-2">
                        <div className="min-w-0">
                            <p className="truncate text-xs font-semibold text-fg-strong">
                                {item.index}. {item.kernaspekt}
                            </p>
                            <p className="truncate text-xs text-fg-muted">
                                Raster {item.layout_grid} · {item.layout_label} · {item.main_element}
                            </p>
                        </div>
                        <Button
                            variant="secondary"
                            size="sm"
                            icon={Download}
                            onClick={() => onDownload(item)}
                            aria-label={`Grafik ${item.index} als PNG herunterladen`}
                            className="shrink-0"
                        >
                            PNG
                        </Button>
                    </div>
                    <img src={item.image_url} alt={item.headline} className="h-auto w-full" />
                    {item.caption && <p className="px-3 py-2 text-xs italic text-fg-secondary">{item.caption}</p>}
                    {item.warnings?.length > 0 && (
                        <ul className="space-y-0.5 bg-warning-soft px-3 py-2 text-xs text-warning">
                            {item.warnings.map((warning, i) => (
                                <li key={i}>{warning}</li>
                            ))}
                        </ul>
                    )}
                </div>
            ))}
        </section>
    );
}

function SentimentView({ result }: { result: SentimentResult }) {
    const overall = OVERALL[result.overall_sentiment] ?? OVERALL.neutral;
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-3 rounded-xl bg-surface-subtle p-4">
                <span className="text-sm font-semibold text-fg-secondary">Gesamtstimmung</span>
                <Badge tone={overall.tone} icon={overall.icon} className="px-3 py-1 text-sm">
                    {overall.label}
                </Badge>
            </div>

            {result.summary && (
                <p className="rounded-xl border border-primary/30 bg-primary-soft p-4 text-sm font-medium leading-relaxed text-fg">
                    {result.summary}
                </p>
            )}

            {result.stocks?.length > 0 && (
                <section aria-labelledby="ag-sentiment-stocks">
                    <h3 id="ag-sentiment-stocks" className={cn(SECTION_TITLE, 'mb-3')}>
                        Auswirkungen auf einzelne Aktien
                    </h3>
                    <ul className="flex flex-wrap gap-3">
                        {result.stocks.map((stock, i) => {
                            const impact = IMPACT[stock.impact] ?? IMPACT.neutral;
                            const Icon = impact.icon;
                            return (
                                <li key={i} className={cn('rounded-lg border px-4 py-2', impact.tile)}>
                                    <p className="flex items-center gap-2 text-sm font-semibold text-fg-strong">
                                        <Icon className={cn('h-4 w-4 shrink-0', impact.text)} aria-hidden="true" />
                                        {stock.name}
                                        <span className="sr-only">: {impact.label}</span>
                                    </p>
                                    <p className="mt-1 text-xs text-fg-secondary">{stock.reason}</p>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            )}

            <section aria-labelledby="ag-sentiment-markets">
                <h3 id="ag-sentiment-markets" className={cn(SECTION_TITLE, 'mb-3')}>
                    Auswirkungen nach Marktbereich
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {result.impacts.map((item, i) => {
                        const impact = IMPACT[item.impact] ?? IMPACT.neutral;
                        const Icon = impact.icon;
                        return (
                            <div key={i} className={cn('rounded-xl border p-4', impact.tile)}>
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <h4 className="font-semibold text-fg-strong">{item.category}</h4>
                                    <span
                                        className={cn('inline-flex items-center gap-1 text-sm font-semibold', impact.text)}
                                    >
                                        <Icon className="h-4 w-4" aria-hidden="true" />
                                        {impact.label}
                                    </span>
                                </div>
                                <ul className="space-y-1">
                                    {item.key_points.map((point, j) => (
                                        <li key={j} className="flex items-start gap-2 text-sm text-fg">
                                            <span className="text-fg-muted" aria-hidden="true">
                                                •
                                            </span>
                                            <span>{point}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}
